/**
 * ProposalMoveTo — the unified "Move to..." menu on a proposal card / row.
 *
 * Shows only destinations that are valid from the proposal's current column.
 * Simple moves (Sent / Engaged / Stalled) call set_manual_stage. Status-changing
 * moves (Won, Lost) open a modal so the user provides the required input
 * (acceptance confirmation, lost reason).
 *
 * The whole point: one consistent action from anywhere on the board, with the
 * proper workflow attached to whatever the user picks.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import { bucketProposal } from './ProposalsBoardConfig';
import { fnErrorMessage } from '../../../../lib/functionError';

type Row = Record<string, any>;  // eslint-disable-line @typescript-eslint/no-explicit-any
type Stage = 'sent' | 'engaged' | 'stalled' | 'interested';

interface MoveOption {
  id: string;
  label: string;
  icon: string;
  kind: 'manual_stage' | 'mark_lost' | 'mark_accepted_admin' | 'unmark_accepted' | 're_engage' | 'submit' | 'open_share' | 'recall_to_drafting';
  manualStage?: Stage;
}

interface ProposalMoveToProps {
  proposal: Row;
  supabase: SupabaseClient;
  isAmbassador: boolean;
  onMoved: () => void;             // refresh proposals list
  onOpenShare: () => void;         // open existing Share modal
  onSubmitForApproval: () => void; // existing Submit-for-approval handler
}

export default function ProposalMoveTo({
  proposal, supabase, isAmbassador, onMoved, onOpenShare, onSubmitForApproval,
}: ProposalMoveToProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [showAccepted, setShowAccepted] = useState(false);
  const [showUnmarkAccepted, setShowUnmarkAccepted] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Portal-positioned menu. Anchored to the trigger's on-screen rect with
  // position: fixed so the column-body's overflow-y: auto can't clip it.
  // ready=false on first paint while we measure; flips to true once we
  // know the menu's own height and can flip up if there's no room below.
  const [pos, setPos] = useState<{ top: number; left: number; ready: boolean }>({ top: 0, left: 0, ready: false });

  useLayoutEffect(() => {
    if (!open) {
      setPos({ top: 0, left: 0, ready: false });
      return;
    }
    if (!triggerRef.current || !menuRef.current) return;
    const tr = triggerRef.current.getBoundingClientRect();
    const mr = menuRef.current.getBoundingClientRect();
    const gutter = 4;
    let top = tr.bottom + gutter;
    const left0 = tr.right - mr.width;
    if (top + mr.height > window.innerHeight - 8 && tr.top - mr.height - gutter >= 8) {
      top = tr.top - mr.height - gutter;
    }
    let left = left0;
    if (left < 8) left = 8;
    if (left + mr.width > window.innerWidth - 8) left = window.innerWidth - mr.width - 8;
    setPos({ top, left, ready: true });
  }, [open]);

  // Click-outside (covers both the trigger and the portal-rendered menu)
  // plus scroll/resize so the menu can't drift off-anchor.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onShift() { setOpen(false); }
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onShift, true);
    window.addEventListener('resize', onShift);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onShift, true);
      window.removeEventListener('resize', onShift);
    };
  }, [open]);

  const currentColumn = bucketProposal(proposal);
  const options = getOptionsFor(currentColumn, proposal, isAmbassador);

  async function handlePick(opt: MoveOption) {
    setOpen(false);
    if (opt.kind === 'open_share')   { onOpenShare(); return; }
    if (opt.kind === 'submit')       { onSubmitForApproval(); return; }
    if (opt.kind === 'mark_lost')    { setShowLost(true); return; }
    if (opt.kind === 'mark_accepted_admin') { setShowAccepted(true); return; }
    if (opt.kind === 'unmark_accepted') { setShowUnmarkAccepted(true); return; }

    if (opt.kind === 'manual_stage' && opt.manualStage) {
      setBusy(true);
      try {
        const { error } = await supabase.functions.invoke('pos-proposal-manage', {
          body: { action: 'set_manual_stage', id: proposal.id, manual_stage: opt.manualStage },
        });
        if (error) throw error;
        onMoved();
      } catch (err) {
        alert(await fnErrorMessage(err, 'Failed to move card'));
      } finally {
        setBusy(false);
      }
    }

    if (opt.kind === 're_engage') {
      setBusy(true);
      try {
        const { error } = await supabase.functions.invoke('pos-proposal-manage', {
          body: { action: 're_engage', id: proposal.id },
        });
        if (error) throw error;
        onMoved();
      } catch (err) {
        alert(await fnErrorMessage(err, 'Failed to re-engage'));
      } finally {
        setBusy(false);
      }
    }

    if (opt.kind === 'recall_to_drafting') {
      const reason = prompt(
        'Why are you recalling this proposal?\n(Optional — adds context to the timeline.)',
        '',
      );
      // prompt returns null on cancel, '' on empty submit. Treat null as abort.
      if (reason === null) return;
      if (!confirm(
        'Recall this proposal to drafting?\n\n' +
        'This wipes sent / view history and lets you edit + resend. The audit trail keeps a Recalled event.',
      )) return;

      setBusy(true);
      try {
        const { error } = await supabase.functions.invoke('pos-proposal-manage', {
          body: { action: 'recall_to_drafting', id: proposal.id, reason: reason.trim() || null },
        });
        if (error) throw error;
        onMoved();
      } catch (err) {
        alert(await fnErrorMessage(err, 'Failed to recall to drafting'));
      } finally {
        setBusy(false);
      }
    }
  }

  if (options.length === 0) return null;

  return (
    <>
      <span
        ref={triggerRef}
        className="action-icon"
        title="Move to..."
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        style={{ display: 'inline-block' }}
      >
        🔄
      </span>
      {open && createPortal(
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            zIndex: 1000,
            visibility: pos.ready ? 'visible' : 'hidden',
            background: 'var(--color-card-bg, #fff)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            padding: '0.25rem 0',
            minWidth: '200px',
            fontSize: 'var(--text-base)',
          }}
        >
          <div style={{ padding: '0.375rem 0.625rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--color-border)' }}>
            Move to
          </div>
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); handlePick(opt); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '0.5rem 0.625rem',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: busy ? 'not-allowed' : 'pointer',
                fontSize: 'var(--text-base)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}

      {showLost && (
        <MarkLostModal
          proposal={proposal}
          supabase={supabase}
          onClose={() => setShowLost(false)}
          onDone={() => { setShowLost(false); onMoved(); }}
        />
      )}
      {showAccepted && (
        <MarkAcceptedAdminModal
          proposal={proposal}
          supabase={supabase}
          onClose={() => setShowAccepted(false)}
          onDone={() => { setShowAccepted(false); onMoved(); }}
        />
      )}
      {showUnmarkAccepted && (
        <UnmarkAcceptedModal
          proposal={proposal}
          supabase={supabase}
          onClose={() => setShowUnmarkAccepted(false)}
          onDone={() => { setShowUnmarkAccepted(false); onMoved(); }}
        />
      )}
    </>
  );
}

// =============================================================================
// Contextual options per column
// =============================================================================

function getOptionsFor(currentColumn: string, proposal: Row, isAmbassador: boolean): MoveOption[] {
  const status = proposal.status as string;

  // Drafting cards: Submit (ambassador) or open Share modal
  if (currentColumn === 'drafting') {
    const opts: MoveOption[] = [];
    if (isAmbassador && status === 'draft') {
      opts.push({ id: 'submit', label: 'Submit for review', icon: '📤', kind: 'submit' });
    }
    opts.push({ id: 'open_share', label: 'Send to customer (Share modal)', icon: '📨', kind: 'open_share' });
    return opts;
  }

  // Awaiting Review: admin Approve/Reject — handled by existing icons, no Move To options here
  if (currentColumn === 'awaiting_review') return [];

  // Customer band — full kit (admin only — ambassadors don't pin or close)
  if (
    currentColumn === 'sent' ||
    currentColumn === 'engaged' ||
    currentColumn === 'stalled' ||
    currentColumn === 'interested'
  ) {
    const opts: MoveOption[] = [];
    if (isAmbassador) return opts;
    if (currentColumn !== 'sent')       opts.push({ id: 'sent',       label: 'Sent',       icon: '📨', kind: 'manual_stage', manualStage: 'sent' });
    if (currentColumn !== 'engaged')    opts.push({ id: 'engaged',    label: 'Engaged',    icon: '🔥', kind: 'manual_stage', manualStage: 'engaged' });
    if (currentColumn !== 'stalled')    opts.push({ id: 'stalled',    label: 'Stalled',    icon: '🐌', kind: 'manual_stage', manualStage: 'stalled' });
    if (currentColumn !== 'interested') opts.push({ id: 'interested', label: 'Interested', icon: '✋', kind: 'manual_stage', manualStage: 'interested' });
    opts.push({ id: 'won',  label: 'Won (mark accepted)', icon: '✍️', kind: 'mark_accepted_admin' });
    opts.push({ id: 'lost', label: 'Lost (mark closed)',  icon: '❌', kind: 'mark_lost' });
    opts.push({ id: 'recall', label: 'Recall to drafting', icon: '↪️', kind: 'recall_to_drafting' });
    return opts;
  }

  // Won — admins can reverse an incorrect acceptance back to the customer band.
  if (currentColumn === 'won') {
    if (isAmbassador) return [];
    return [
      { id: 'unmark_accepted', label: 'Unmark as Won (revert)', icon: '↩️', kind: 'unmark_accepted' },
    ];
  }

  // Lost — re-engage or pull all the way back to drafting
  if (currentColumn === 'lost') {
    if (isAmbassador) return [];
    return [
      { id: 're_engage', label: 'Re-engage (back to Engaged)', icon: '↩️', kind: 're_engage' },
      { id: 'recall', label: 'Recall to drafting', icon: '↪️', kind: 'recall_to_drafting' },
    ];
  }

  return [];
}

// =============================================================================
// Mark Lost modal
// =============================================================================

const LOST_REASONS = [
  'Budget',
  'Timing',
  'Scope mismatch',
  'Competitor',
  'Decision-maker changed',
  'No response',
  'Other',
];

interface ModalProps {
  proposal: Row;
  supabase: SupabaseClient;
  onClose: () => void;
  onDone: () => void;
}

function MarkLostModal({ proposal, supabase, onClose, onDone }: ModalProps) {
  const [reason, setReason] = useState(LOST_REASONS[0]);
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    const composed = extra.trim() ? `${reason}: ${extra.trim()}` : reason;
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('pos-proposal-manage', {
        body: { action: 'mark_lost', id: proposal.id, reason: composed },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);
      onDone();
    } catch (err) {
      setError(await fnErrorMessage(err, 'Failed to mark as lost'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Mark as lost</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', marginBottom: '0.75rem' }}>
            <code>{proposal.reference_code}</code>
            {proposal.property_name && <> &middot; {proposal.property_name}</>}
          </div>
          <label className="form-label">Reason</label>
          <select className="form-input mb-md" value={reason} onChange={(e) => setReason(e.target.value)}>
            {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="form-label">Notes (optional)</label>
          <textarea
            className="form-input"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Anything that helps re-engage this customer in future"
          />
          {error && <div className="form-error mt-sm">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Saving…' : 'Mark as lost'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Mark Accepted Admin modal — "customer signed offline" path
// =============================================================================

function MarkAcceptedAdminModal({ proposal, supabase, onClose, onDone }: ModalProps) {
  const [acceptedAt, setAcceptedAt] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('pos-proposal-manage', {
        body: { action: 'mark_accepted_admin', id: proposal.id, accepted_at: acceptedAt, note: note.trim() || null },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);
      onDone();
    } catch (err) {
      setError(await fnErrorMessage(err, 'Failed to mark as accepted'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Mark as accepted</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning mb-md">
            Use this when the customer has accepted the proposal off-platform (signed in person, agreed by email, etc). The system will record acceptance on their behalf with your name attached.
          </div>
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', marginBottom: '0.75rem' }}>
            <code>{proposal.reference_code}</code>
            {proposal.property_name && <> &middot; {proposal.property_name}</>}
          </div>
          <label className="form-label">Accepted on</label>
          <input
            type="date"
            className="form-input mb-md"
            value={acceptedAt}
            onChange={(e) => setAcceptedAt(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
          <label className="form-label">Note (optional)</label>
          <textarea
            className="form-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="How did the customer accept? Any context worth keeping."
          />
          {error && <div className="form-error mt-sm">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Saving…' : 'Confirm acceptance'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Unmark Accepted modal — reverses an incorrect Won. Reason is required so
// the timeline records why. The frozen snapshot + PDF are kept (audit only).
// =============================================================================

function UnmarkAcceptedModal({ proposal, supabase, onClose, onDone }: ModalProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('A reason is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('pos-proposal-manage', {
        body: { action: 'unmark_accepted', id: proposal.id, reason: trimmed },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);
      onDone();
    } catch (err) {
      setError(await fnErrorMessage(err, 'Failed to unmark acceptance'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Unmark as Won</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning mb-md">
            This reverses the customer&apos;s acceptance. The proposal returns to the
            active board (Engaged / Sent depending on whether they had viewed it)
            and the lock is removed so it can be edited again.
            <br /><br />
            The originally accepted snapshot and PDF are kept on file for audit.
          </div>
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', marginBottom: '0.75rem' }}>
            <code>{proposal.reference_code}</code>
            {proposal.property_name && <> &middot; {proposal.property_name}</>}
          </div>
          <label className="form-label">Reason (required)</label>
          <textarea
            className="form-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. accepted in error, customer changed their mind, data correction"
          />
          {error && <div className="form-error mt-sm">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Saving…' : 'Confirm unmark'}
          </button>
        </div>
      </div>
    </div>
  );
}
